import { usePendingOfferPollingStore } from '@/lib/offerStatusPolling/usePendingOfferPollingStore';
import { useServerSideEventListeners } from '@/lib/serverSentEvents/useServerSideEventListeners';

export interface MarketStatsMeta {
  marketUuid: string;
  /** SSE path — numeric taker division id; keep passing this during the uuid transition */
  takerId: number;
  /** Polling path — provide for precise matching against the polling store once available */
  takerDivisionUuid?: string;
  makerOrganizationUuid?: string;
}

/**
 * Unified hook for checking whether a market is currently calculating stats.
 * Aggregates both SSE (push) and polling (pull) pending-market signals so callers
 * don't need to know which mechanism triggered the loading state.
 */
export const useMarketStatsStatus = () => {
  const {
    listenToMarketStats: sseListenToMarketStats,
    listenToAllMarketStats: sseListenToAll,
    ...rest
  } = useServerSideEventListeners();
  const { hasPendingMarkets, getPendingMarkets } = usePendingOfferPollingStore();

  const listenToAllMarketStats = (): boolean => sseListenToAll() || hasPendingMarkets();

  const listenToMarketStats = (tmMeta: MarketStatsMeta[]): boolean => {
    const sseMatch =
      sseListenToMarketStats(tmMeta.map(({ marketUuid, takerId }) => ({ marketUuid, takerId }))) ?? false;

    const pendingMarkets = getPendingMarkets();
    const pollingMatch = pendingMarkets.some((pm) =>
      tmMeta.some(({ marketUuid, takerDivisionUuid, makerOrganizationUuid }) => {
        if (takerDivisionUuid && makerOrganizationUuid) {
          // Precise match when UUID fields are provided
          return (
            pm.marketUuid === marketUuid &&
            pm.takerDivisionUuid === takerDivisionUuid &&
            pm.makerOrganizationUuid === makerOrganizationUuid
          );
        }

        // Fallback during transition: match on marketUuid alone
        return pm.marketUuid === marketUuid;
      })
    );

    return sseMatch || pollingMatch;
  };

  return { listenToMarketStats, listenToAllMarketStats, ...rest };
};
